import React, { useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  parse, format, addDays, subDays, min,
} from 'date-fns';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../shared/_TBComponents/Button';
import DatePicker from '../../../../shared/_TBComponents/DatePicker';
import Icon from '../../../../shared/_TBComponents/Icon';
import Modal from '../../../../shared/_TBComponents/Modal';
import SelectBox from '../../../../shared/_TBComponents/SelectBox';
import Text from '../../../../shared/_TBComponents/Text';
import Toggle from '../../../../shared/_TBComponents/Toggle';
import { showSuccessToast } from '../../../../shared/utils/toastManager';

import AddProgramModal from './modals/AddProgramModal';
import BulkIssuesModal from './modals/BulkIssuesModal';
import ConfirmModal from './modals/ConfirmModal';
import {
  bulkAssignPrograms,
  bulkEmailAssessments,
  bulkUpdateCalendars,
  bulkUpdateGroups,
  deleteUsers,
  moveUsers,
} from '../../ducks/navigatorActions';

const BULK_GROUP_ACTIONS = [
  { id: 0, name: 'Add the following group' },
  { id: 1, name: 'Remove the following group' },
];

const BULK_EMAIL_RECIPIENT_OPTIONS = [
  { id: 0, name: 'Selected Athletes Only' },
  { id: 1, name: 'Guardians/Parents of Selected Athletes' },
  { id: 2, name: 'Selected Athletes and their Guardians/Parents' },
];

const BULK_CALENDAR_ACTION_OPTIONS = [
  { id: 0, name: 'Change to the following calendar assignment' },
  { id: 1, name: 'End calendar assignments with empty end date' },
];

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 100%;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 20px;
`;

const SelectWrapper = styled.div`
  margin-bottom: 24px;
`;

const DateRangeRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  margin-bottom: 16px;
`;

const DatePickerField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const CalendarOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding-left: ${(props) => `${props.level * 16}px`};
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-top: 8px;
`;

const DateOffsetContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const SelectProgramButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #EEEEEE;
  border-radius: 6px;
  margin-bottom: 24px;
  background: #FFF;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #333333;
  &:hover { opacity: 0.7; }
`;

const ManageAthletesBulkActions = ({
  bulkAction,
  bulkSelectedIds,
  onClose,
  onSetBulkAction,
  accountCode,
  accountsList,
  athletes,
  groups,
  calendars,
  programs,
  onRefetch,
}) => {
  const dispatch = useDispatch();

  const isDeletingUsers = useSelector((state) => state.navigator.ui.isDeletingUsers);
  const isBulkUpdatingGroups = useSelector((state) => state.navigator.ui.isBulkUpdatingGroups);
  const isBulkUpdatingCalendars = useSelector(
    (state) => state.navigator.ui.isBulkUpdatingCalendars,
  );
  const isAssigningAthletePrograms = useSelector(
    (state) => state.navigator.ui.isAssigningAthletePrograms,
  );

  const [bulkGroupAction, setBulkGroupAction] = useState(BULK_GROUP_ACTIONS[0]);
  const [bulkGroupSelection, setBulkGroupSelection] = useState(null);
  const [bulkEmailRecipient, setBulkEmailRecipient] = useState(BULK_EMAIL_RECIPIENT_OPTIONS[0]);
  const [bulkEmailStartDate, setBulkEmailStartDate] = useState(
    format(subDays(new Date(), 6), 'MM/dd/yyyy'),
  );
  const [bulkEmailEndDate, setBulkEmailEndDate] = useState(format(new Date(), 'MM/dd/yyyy'));
  const [bulkCalendarAction, setBulkCalendarAction] = useState(BULK_CALENDAR_ACTION_OPTIONS[0]);
  const [bulkCalendarSelection, setBulkCalendarSelection] = useState(null);
  const [bulkCalendarStartDate, setBulkCalendarStartDate] = useState(format(new Date(), 'MM/dd/yyyy'));
  const [bulkCalendarEndDate, setBulkCalendarEndDate] = useState('');
  const [bulkCalendarDateOffset, setBulkCalendarDateOffset] = useState(null);
  const [calendarIssuesResult, setCalendarIssuesResult] = useState(null);
  const [bulkProgramSelection, setBulkProgramSelection] = useState([]);
  const [isBulkAddProgramOpen, setIsBulkAddProgramOpen] = useState(false);
  const [bulkMoveAccountSelection, setBulkMoveAccountSelection] = useState(null);
  const [isMovingUsers, setIsMovingUsers] = useState(false);

  const moveAccountOptions = (accountsList || []).filter((account) => account.id !== accountCode);

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [calendars],
  );

  const getCalendarLevel = (calendar) => {
    let level = 0;
    let currentParentId = calendar.parentCalendarId;
    const visited = new Set();
    while (currentParentId && calendarMap[currentParentId]) {
      if (visited.has(currentParentId)) break;
      visited.add(currentParentId);
      level += 1;
      currentParentId = calendarMap[currentParentId].parentCalendarId;
    }
    return level;
  };

  const formatCalendarOptionLabel = (option, { context }) => {
    const level = getCalendarLevel(option);
    const isChild = level > 0;
    if (context === 'value') return option.name;
    return (
      <CalendarOptionRow level={level}>
        {isChild ? (
          <Icon
            icon='sub-arrow'
            size={12}
            color='#929292'
            styledProps={{ marginBottom: '6px' }}
          />
        ) : null}
        <Text styledTextProps={{ color: '#333333', fontWeight: 600 }}>{option.name}</Text>
      </CalendarOptionRow>
    );
  };

  const closeDeleteModal = () => { onClose(); };

  const handleDeleteSave = () => {
    dispatch(deleteUsers(accountCode, bulkSelectedIds, {
      onSuccess: () => {
        showSuccessToast(`${bulkSelectedIds.length} ${bulkSelectedIds.length === 1 ? 'athlete' : 'athletes'} deleted successfully.`);
        onRefetch();
        onClose();
      },
    }));
  };

  const closeGroupsModal = () => {
    setBulkGroupAction(BULK_GROUP_ACTIONS[0]);
    setBulkGroupSelection(null);
    onClose();
  };

  const handleBulkGroupSave = () => {
    dispatch(bulkUpdateGroups(
      accountCode,
      bulkSelectedIds,
      [bulkGroupSelection.id],
      bulkGroupAction.id === 0 ? 'add' : 'remove',
      {
        onSuccess: () => {
          showSuccessToast('Groups updated successfully.');
          onRefetch();
          closeGroupsModal();
        },
      },
    ));
  };

  const closeEmailModal = () => {
    setBulkEmailRecipient(BULK_EMAIL_RECIPIENT_OPTIONS[0]);
    setBulkEmailStartDate(format(subDays(new Date(), 6), 'MM/dd/yyyy'));
    setBulkEmailEndDate(format(new Date(), 'MM/dd/yyyy'));
    onClose();
  };

  const handleBulkEmailSave = () => {
    dispatch(bulkEmailAssessments(
      accountCode,
      bulkSelectedIds,
      bulkEmailRecipient.id,
      bulkEmailStartDate,
      bulkEmailEndDate,
      {
        onSuccess: () => {
          showSuccessToast('Performance assessment emails sent successfully.');
          closeEmailModal();
        },
      },
    ));
  };

  const closeCalendarsModal = () => {
    setBulkCalendarAction(BULK_CALENDAR_ACTION_OPTIONS[0]);
    setBulkCalendarSelection(null);
    setBulkCalendarStartDate(format(new Date(), 'MM/dd/yyyy'));
    setBulkCalendarEndDate('');
    setBulkCalendarDateOffset(null);
    onClose();
  };

  const handleBulkCalendarSave = () => {
    dispatch(bulkUpdateCalendars(
      accountCode,
      bulkSelectedIds,
      bulkCalendarAction.id === 0 ? bulkCalendarSelection?.id : null,
      bulkCalendarAction.id === 0 ? 'assign' : 'end',
      bulkCalendarStartDate,
      bulkCalendarEndDate,
      bulkCalendarDateOffset,
      {
        onSuccess: (data) => {
          const issues = data?.issues || [];
          const totalCount = bulkSelectedIds.length;
          const successCount = data?.userIds?.length ?? totalCount;

          if (issues.length > 0) {
            setCalendarIssuesResult({ issues, successCount, totalCount });
          } else if (bulkCalendarAction.id === 0) {
            const startFormatted = format(parse(bulkCalendarStartDate, 'MM/dd/yyyy', new Date()), 'MMMM d, yyyy');
            showSuccessToast(`Calendar assignments updated successfully. This assignment will start on ${startFormatted}.`);
          } else {
            showSuccessToast('Calendar assignments updated successfully.');
          }
          onRefetch();
          closeCalendarsModal();
        },
      },
    ));
  };

  const closeProgramsModal = () => {
    setBulkProgramSelection([]);
    onClose();
  };

  const handleBulkProgramSave = () => {
    dispatch(bulkAssignPrograms(
      accountCode,
      bulkSelectedIds,
      bulkProgramSelection,
      {
        onSuccess: () => {
          showSuccessToast('Programs assigned successfully.');
          onRefetch();
          closeProgramsModal();
        },
      },
    ));
  };

  const closeMoveModal = () => {
    setBulkMoveAccountSelection(null);
    onClose();
  };

  const handleBulkMoveSave = () => {
    setIsMovingUsers(true);
    dispatch(moveUsers(
      accountCode,
      bulkSelectedIds,
      bulkMoveAccountSelection.id,
      {
        onSuccess: () => {
          showSuccessToast('Athletes moved successfully.');
          onRefetch();
          closeMoveModal();
          setIsMovingUsers(false);
        },
        onError: () => {
          setIsMovingUsers(false);
        },
      },
    ));
  };

  return (
    <>
      <ConfirmModal
        isOpen={bulkAction === 'deleteAthletes'}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteSave}
        title='Delete Athletes'
        message={`Are you sure you want to delete the ${bulkSelectedIds.length} selected ${bulkSelectedIds.length === 1 ? 'athlete' : 'athletes'}? This action cannot be undone.`}
        isLoading={isDeletingUsers}
      />

      <Modal
        isOpen={bulkAction === 'changeGroups'}
        onRequestClose={closeGroupsModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='480px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Change Groups
          </Text>
          <ToggleRow>
            <Text styledTextProps={{ fontSize: '14px', color: '#333333' }}>
              What would you like to do with the selected athletes?
            </Text>
            <SelectBox
              options={BULK_GROUP_ACTIONS}
              value={bulkGroupAction}
              onChangeFunction={(opt) => setBulkGroupAction(opt)}
              styledProps={{ border: '1px solid #EEEEEE' }}
              isClearable={false}
              isSearchable={false}
              height='auto'
            />
          </ToggleRow>
          <SelectWrapper>
            <SelectBox
              title='Groups'
              options={groups.map(({ id, name }) => ({ id, name }))}
              value={bulkGroupSelection}
              onChangeFunction={(opt) => setBulkGroupSelection(opt)}
              placeholder='Select group'
              styledProps={{ border: '1px solid #EEEEEE' }}
              titleStyledProps={{ fontWeight: 700 }}
            />
          </SelectWrapper>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={closeGroupsModal}
            />
            <Button
              name={isBulkUpdatingGroups ? 'Saving...' : 'Save'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={!bulkGroupSelection || isBulkUpdatingGroups}
              onClick={handleBulkGroupSave}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={bulkAction === 'emailAssessments'}
        onRequestClose={closeEmailModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='520px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Email Performance Assessments
          </Text>
          <ToggleRow>
            <Text styledTextProps={{ fontSize: '14px', color: '#333333' }}>
              Who you would like to send the Weekly Performance Assessments to?
            </Text>
            <SelectBox
              options={BULK_EMAIL_RECIPIENT_OPTIONS}
              value={bulkEmailRecipient}
              onChangeFunction={(opt) => setBulkEmailRecipient(opt)}
              styledProps={{ border: '1px solid #EEEEEE' }}
              isClearable={false}
              isSearchable={false}
              height='auto'
            />
          </ToggleRow>
          <SelectWrapper>
            <Text styledTextProps={{ fontSize: '16px', marginBottom: '8px' }}>
              Select dates for the Assessment
            </Text>
            <DateRangeRow>
              <DatePickerField>
                <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Start Date</Text>
                <DatePicker
                  singleDate
                  startDate={bulkEmailStartDate ? parse(bulkEmailStartDate, 'MM/dd/yyyy', new Date()) : null}
                  setStartDate={(date) => {
                    if (!date) { setBulkEmailStartDate(''); return; }
                    const newStart = date;
                    setBulkEmailStartDate(format(newStart, 'MM/dd/yyyy'));
                    if (bulkEmailEndDate) {
                      const currentEnd = parse(bulkEmailEndDate, 'MM/dd/yyyy', new Date());
                      if (currentEnd < newStart) {
                        setBulkEmailEndDate(format(newStart, 'MM/dd/yyyy'));
                      } else if (currentEnd > addDays(newStart, 6)) {
                        setBulkEmailEndDate(format(addDays(newStart, 6), 'MM/dd/yyyy'));
                      }
                    }
                  }}
                  maxDate={new Date()}
                  placeholderText='Select start date'
                  textColor='#333333'
                />
              </DatePickerField>
              <DatePickerField>
                <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>End Date</Text>
                <DatePicker
                  singleDate
                  startDate={bulkEmailEndDate ? parse(bulkEmailEndDate, 'MM/dd/yyyy', new Date()) : null}
                  setStartDate={(date) => setBulkEmailEndDate(date ? format(date, 'MM/dd/yyyy') : '')}
                  minDate={bulkEmailStartDate ? parse(bulkEmailStartDate, 'MM/dd/yyyy', new Date()) : undefined}
                  maxDate={bulkEmailStartDate ? min([addDays(parse(bulkEmailStartDate, 'MM/dd/yyyy', new Date()), 6), new Date()]) : new Date()}
                  placeholderText='Select end date'
                  textColor='#333333'
                />
              </DatePickerField>
            </DateRangeRow>
            <Text styledTextProps={{ fontSize: '14px', color: '#9E9E9E' }}>
              Note: The Weekly Performance Assessment emails can only cover
              a period of up to 7 days.
            </Text>
          </SelectWrapper>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={closeEmailModal}
            />
            <Button
              name='Send'
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={!bulkEmailStartDate || !bulkEmailEndDate}
              onClick={handleBulkEmailSave}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={bulkAction === 'changeCalendars'}
        onRequestClose={closeCalendarsModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='520px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Change Calendars
          </Text>
          <ToggleRow>
            <Text styledTextProps={{ fontSize: '14px', color: '#333333' }}>
              What would you like to do with the selected athletes?
            </Text>
            <SelectBox
              options={BULK_CALENDAR_ACTION_OPTIONS}
              value={bulkCalendarAction}
              onChangeFunction={(opt) => {
                setBulkCalendarAction(opt);
                setBulkCalendarSelection(null);
                setBulkCalendarStartDate('');
                setBulkCalendarEndDate('');
                setBulkCalendarDateOffset(null);
              }}
              styledProps={{ border: '1px solid #EEEEEE' }}
              isClearable={false}
              isSearchable={false}
              height='auto'
            />
          </ToggleRow>
          {bulkCalendarAction.id === 0 && (
            <>
              <SelectWrapper>
                <SelectBox
                  title='Calendar'
                  options={calendars}
                  value={bulkCalendarSelection}
                  formatOptionLabel={formatCalendarOptionLabel}
                  onChangeFunction={(opt) => setBulkCalendarSelection(opt)}
                  placeholder='Select a Calendar'
                  styledProps={{ border: '1px solid #EEEEEE' }}
                  titleStyledProps={{ fontWeight: 700 }}
                  height='auto'
                />
              </SelectWrapper>
              {bulkCalendarSelection && (
                <>
                  <DateRangeRow>
                    <DatePickerField>
                      <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>Start Date</Text>
                      <DatePicker
                        singleDate
                        startDate={bulkCalendarStartDate ? parse(bulkCalendarStartDate, 'MM/dd/yyyy', new Date()) : null}
                        setStartDate={(date) => {
                          setBulkCalendarStartDate(date ? format(date, 'MM/dd/yyyy') : '');
                          if (date && bulkCalendarEndDate) {
                            const end = parse(bulkCalendarEndDate, 'MM/dd/yyyy', new Date());
                            if (end < date) setBulkCalendarEndDate('');
                          }
                          if (date && bulkCalendarDateOffset) {
                            const offset = parse(bulkCalendarDateOffset, 'MM/dd/yyyy', new Date());
                            if (offset >= date) setBulkCalendarDateOffset('');
                          }
                        }}
                        maxDate={null}
                        placeholderText='Select start date'
                        textColor='#333333'
                      />
                    </DatePickerField>
                    <DatePickerField>
                      <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                        End Date
                        {' '}
                        <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>(Leave blank if no end date)</span>
                      </Text>
                      <DatePicker
                        singleDate
                        startDate={bulkCalendarEndDate ? parse(bulkCalendarEndDate, 'MM/dd/yyyy', new Date()) : null}
                        setStartDate={(date) => setBulkCalendarEndDate(date ? format(date, 'MM/dd/yyyy') : '')}
                        minDate={bulkCalendarStartDate ? parse(bulkCalendarStartDate, 'MM/dd/yyyy', new Date()) : undefined}
                        maxDate={null}
                        placeholderText='Select end date'
                        textColor='#333333'
                      />
                    </DatePickerField>
                  </DateRangeRow>
                  <DateOffsetContainer>
                    <ToggleContainer>
                      <Toggle
                        defaultChecked={Boolean(bulkCalendarDateOffset)}
                        onChange={(e) => {
                          setBulkCalendarDateOffset(e.target.checked ? '' : null);
                        }}
                        onHoverColor='#0DCC8A'
                        onColor='#0DCC8A'
                        offColor='#E0E0E0'
                        thumbColorOff
                        thumbBorderOff
                        width='auto'
                      />
                      <Text
                        styledTextProps={{ fontSize: '14px', color: '#333333' }}
                        styledWrapperProps={{ marginTop: '2px' }}
                      >
                        Enable Calendar Offset (Set Specific Date as First Workout)
                      </Text>
                    </ToggleContainer>
                    {bulkCalendarDateOffset !== null && (
                      <DatePickerField>
                        <Text styledTextProps={{ fontSize: '14px', fontWeight: 700 }}>
                          Offset Date
                          {' '}
                          <span style={{ fontSize: '12px', color: '#9E9E9E', fontWeight: 400 }}>(Optional)</span>
                        </Text>
                        <DatePicker
                          singleDate
                          startDate={bulkCalendarDateOffset ? parse(bulkCalendarDateOffset, 'MM/dd/yyyy', new Date()) : null}
                          setStartDate={(date) => setBulkCalendarDateOffset(date ? format(date, 'MM/dd/yyyy') : '')}
                          maxDate={bulkCalendarStartDate ? subDays(parse(bulkCalendarStartDate, 'MM/dd/yyyy', new Date()), 1) : null}
                          placeholderText='Select offset date'
                          textColor='#333333'
                        />
                      </DatePickerField>
                    )}
                  </DateOffsetContainer>
                </>
              )}
            </>
          )}
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={closeCalendarsModal}
            />
            <Button
              name={isBulkUpdatingCalendars ? 'Saving...' : 'Save'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={
                isBulkUpdatingCalendars
                || (bulkCalendarAction.id === 0
                  && (!bulkCalendarSelection || !bulkCalendarStartDate))
              }
              onClick={handleBulkCalendarSave}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={bulkAction === 'assignPrograms'}
        onRequestClose={closeProgramsModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='400px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Program Assignments
          </Text>
          <Text styledWrapperProps={{ marginBottom: '16px' }}>
            Select the program you would like to add to the selected athletes.
          </Text>
          <SelectProgramButton
            type='button'
            onClick={() => {
              onSetBulkAction(null);
              setIsBulkAddProgramOpen(true);
            }}
          >
            {bulkProgramSelection.length > 0
              ? `${bulkProgramSelection.length} Program${bulkProgramSelection.length !== 1 ? 's' : ''} Selected`
              : 'Select Program'}
            <Icon icon='altrightarrow' size={16} color='#929292' />
          </SelectProgramButton>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={closeProgramsModal}
            />
            <Button
              name={isAssigningAthletePrograms ? 'Saving...' : 'Save'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={bulkProgramSelection.length === 0 || isAssigningAthletePrograms}
              onClick={handleBulkProgramSave}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <Modal
        isOpen={bulkAction === 'moveAccount'}
        onRequestClose={closeMoveModal}
        customBorderRadius='8px'
        customPadding='24px'
        customMaxWidth='480px'
        customMinHeight='200px'
      >
        <ContentWrapper>
          <Text styledTextProps={{ fontSize: '18px', fontWeight: 600, marginBottom: '16px' }}>
            Move Athletes
          </Text>
          <SelectWrapper>
            <Text styledWrapperProps={{ marginBottom: '16px' }}>
              Where would you like to move the selected athletes?
            </Text>
            <SelectBox
              options={moveAccountOptions}
              value={bulkMoveAccountSelection}
              onChangeFunction={(opt) => setBulkMoveAccountSelection(opt)}
              placeholder='Select account to move to'
              styledProps={{ border: '1px solid #EEEEEE' }}
            />
          </SelectWrapper>
          <Footer>
            <Button
              name='Cancel'
              fontSize='14px'
              bgColor='#FFF'
              borderColor='#EEEEEE'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              onClick={closeMoveModal}
            />
            <Button
              name={isMovingUsers ? 'Saving...' : 'Save'}
              fontSize='14px'
              txtColor='#FFF'
              bgColor='#1A1A1A'
              borderColor='#1A1A1A'
              customBorderRadius='6px'
              styledTextProps={{ fontWeight: 600 }}
              disabled={!bulkMoveAccountSelection || isMovingUsers}
              onClick={handleBulkMoveSave}
            />
          </Footer>
        </ContentWrapper>
      </Modal>

      <AddProgramModal
        isOpen={isBulkAddProgramOpen}
        onClose={() => {
          setIsBulkAddProgramOpen(false);
          onSetBulkAction('assignPrograms');
        }}
        programs={programs}
        onSave={(programIds) => {
          setBulkProgramSelection(programIds);
          setIsBulkAddProgramOpen(false);
          onSetBulkAction('assignPrograms');
        }}
      />

      <BulkIssuesModal
        isOpen={!!calendarIssuesResult}
        onClose={() => setCalendarIssuesResult(null)}
        title='Calendar Update Issues'
        issues={calendarIssuesResult?.issues}
        entities={athletes}
        successCount={calendarIssuesResult?.successCount}
        totalCount={calendarIssuesResult?.totalCount}
      />
    </>
  );
};

ManageAthletesBulkActions.propTypes = {
  bulkAction: PropTypes.string,
  bulkSelectedIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ),
  onClose: PropTypes.func.isRequired,
  onSetBulkAction: PropTypes.func.isRequired,
  accountCode: PropTypes.string,
  accountsList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  athletes: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    first: PropTypes.string,
    last: PropTypes.string,
  })),
  groups: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  calendars: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  programs: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  })),
  onRefetch: PropTypes.func.isRequired,
};

ManageAthletesBulkActions.defaultProps = {
  bulkAction: null,
  bulkSelectedIds: [],
  accountCode: null,
  accountsList: [],
  athletes: [],
  groups: [],
  calendars: [],
  programs: [],
};

export default ManageAthletesBulkActions;
